/* eslint-disable react/prop-types */
import React, { type FC, useRef } from 'react'
import { SidePanel } from '@awell/ui-kit/src/components/SidePanel'
import { findChildByType } from '@awell/ui-kit/src/utils/findChildByType'
import { ResizableDivider, ResizablePanel, useResizable } from '@awell/ui-kit/layouts/ResizablePanel'
import classNames from 'classnames'
import { usePathwayContextProvider } from '../PathwayContextProvider'
import { useStyles } from './useStyles'
import { Button, Icon } from '@awell-health/design-system'

const Header: FC = ({ children }) => {
  return <>{children}</>
}

const Content: FC = ({ children }) => {
  return <>{children}</>
}

interface Slots {
  Header: FC
  Content: FC
}

interface PathwaySidePanelLayoutProps {
  showTabs?: boolean
  actionButtons?: JSX.Element | Array<JSX.Element>
}

export const PathwaySidePanelLayout: FC<PathwaySidePanelLayoutProps> & Slots = ({
  showTabs = false,
  actionButtons,
  children
}) => {
  const header = findChildByType(children, Header)
  const content = findChildByType(children, Content)
  const { sidePanelOpen, onClosePanel } = usePathwayContextProvider()
  const panelRef = useRef<HTMLDivElement>(null)

  const { onMouseDown, onMouseUp, onTouchStart, onTouchEnd, dragging, panelWidth, setPanelWidth } = useResizable({
    layoutWidthMultiplier: 0.35,
    onResize: () => null,
    minWidthMultiplier: 0.25,
    maxWidthMultiplier: 0.4,
    panelSide: 'right'
  })
  const classes = useStyles({ panelWidth })

  return (
    <div
      ref={panelRef}
      className={classNames(classes.panelWithDivider, {
        [classes.open]: sidePanelOpen,
        [classes.close]: !sidePanelOpen
      })}
    >
      <ResizableDivider
        transparent
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onMouseUp={onMouseUp}
        dragging={dragging}
        className={classes.panelDivider}
        data-resizable-divider
      />
      <ResizablePanel panelWidth={panelWidth} setPanelWidth={setPanelWidth} className={classes.endPanel}>
        <SidePanel className={classes.sidePanelContainer} hasTabs={showTabs}>
          <SidePanel.Header>
            {header}
            <div className="flex items-center gap-2">
              {actionButtons}
              <Button
                variant="secondary"
                className="bg-white btn-square h-9 w-9 min-h-9 min-w-h-9 rounded-lg hover:bg-slate-100 border-slate-100 hover:border-slate-300 transition-colors duration-200"
                size={null}
                shape="squareSm"
                onClick={() => {
                  onClosePanel()
                }}
              >
                <Icon icon="RiCloseFill" size={20} className="fill-slate-600 hover:fill-slate-800 transition-colors" />
              </Button>
            </div>
          </SidePanel.Header>
          <SidePanel.Content>{content}</SidePanel.Content>
        </SidePanel>
      </ResizablePanel>
    </div>
  )
}

PathwaySidePanelLayout.Header = Header
PathwaySidePanelLayout.Content = Content
